import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gtmService from '../services/gtmService';
import {
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Login as LoginIcon,
  CheckCircle as CheckCircleIcon,
  PhotoCamera as PhotoCameraIcon,
  Security as SecurityIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [userCredentials, setUserCredentials] = useState({ username: '', password: '' });
  
  // WhatsApp verification states
  const [currentStep, setCurrentStep] = useState(0); // 0: form, 1: whatsapp verification, 2: success
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  
  const navigate = useNavigate();

  // Timer for resend countdown
  React.useEffect(() => {
    let interval;
    if (remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime(time => time - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [remainingTime]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // WhatsApp verification functions
  const sendWhatsAppVerification = async (phone) => {
    setIsVerifying(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-verification.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send',
          phone_number: phone
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setCodeSent(true);
        setRemainingTime(300); // 5 minutes
        setError(''); // Clear any previous errors
        return true;
      } else {
        setError(result.error || "فشل في إرسال رمز التحقق");
        return false;
      }
    } catch (error) {
      console.error('Verification send error:', error);
      setError("خطأ في الاتصال بالخادم");
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyWhatsAppCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("يرجى إدخال رمز التحقق المكون من 6 أرقام");
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-verification.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verify',
          phone_number: formData.phone,
          code: verificationCode
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setIsPhoneVerified(true);
        setCurrentStep(2); // Move to final step
        setError('');
        // Proceed with actual signup
        await proceedWithSignup();
      } else {
        setError(result.error || "رمز التحقق غير صحيح");
        setRemainingAttempts(result.remaining_attempts || 0);
        if (result.remaining_attempts === 0) {
          resetVerificationState();
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError("خطأ في التحقق من الرمز");
    } finally {
      setIsVerifying(false);
    }
  };

  const resetVerificationState = () => {
    setVerificationCode("");
    setCodeSent(false);
    setRemainingTime(0);
    setRemainingAttempts(3);
    setCurrentStep(0);
  };

  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    // Format as +964 7XX XXX XXXX
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('964')) {
      const number = cleaned.slice(3);
      return `+964 ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
    }
    return phone;
  };

  const validateForm = () => {
    const { name, phone, city, password, confirmPassword } = formData;
    
    // Required fields validation
    if (!name.trim() || !phone.trim() || !city.trim() || !password.trim()) {
      setError('الاسم ورقم الهاتف والمدينة وكلمة المرور مطلوبة');
      return false;
    }

    // Phone validation (Iraqi phone numbers)
    const phoneRegex = /^(07[3-9]|075)\d{8}$/;
    if (!phoneRegex.test(phone.trim())) {
      setError('يرجى إدخال رقم هاتف عراقي صحيح (07xxxxxxxx)');
      return false;
    }

    // Password validation
    if (password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return false;
    }

    // Confirm password validation
    if (password !== confirmPassword) {
      setError('كلمة المرور وتأكيد كلمة المرور غير متطابقتين');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setError('');
    
    // First, initiate WhatsApp verification
    setCurrentStep(1);
    const success = await sendWhatsAppVerification(formData.phone);
    if (!success) {
      setCurrentStep(0); // Go back to form if failed
    }
  };

  // Separate function for actual signup after verification
  const proceedWithSignup = async () => {
    setLoading(true);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/signup.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          city: formData.city.trim() || '',
          address: formData.address.trim() || '',
          password: formData.password,
          phone_verified: true // Mark as phone verified
        }),
      });

      // Check for server/CORS errors
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        setError("خطأ في الاتصال بالخادم أو إعدادات CORS غير صحيحة.");
        setLoading(false);
        return;
      }

      const data = await res.json();
      
      if (data.success) {
        // Store user data in localStorage (matching login format)
        localStorage.setItem(
          "user",
          JSON.stringify({
            name: data.name || formData.name.trim(),
            phone: data.phone || formData.phone.trim(),
            city: data.city || formData.city.trim(),
            address: data.address || formData.address.trim(),
            username: data.username || formData.phone.trim(), // Username = phone number
            id: data.id || "",
            role: "customer",
            points: data.points || 0,
          })
        );
        
        // Set customer flag
        localStorage.setItem("isCustomer", "true");
        localStorage.setItem('isLoggedIn', 'true');
        
        // Show success dialog with credentials
        const username = data.username || formData.phone.trim();
        setUserCredentials({
          username: username,
          password: formData.password
        });
        setShowSuccessDialog(true);
        
        // Track registration with GTM
        gtmService.trackRegistration('phone');
        
        // Dispatch custom event to notify other components of user login
        window.dispatchEvent(new CustomEvent('userUpdated'));
      } else {
        setError(data.error || "فشل في إنشاء الحساب");
      }
    } catch (err) {
      setError("تعذر الاتصال بالخادم");
    }
    
    setLoading(false);
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    // Navigate to home page after user acknowledges the message
    navigate('/');
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      // Success - could add toast notification here if needed
    }).catch(err => {
      // Handle copy failure silently or show user-friendly message
    });
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default', 
      pt: { xs: 8, sm: 10, md: 12 },
      pb: { xs: 4, sm: 6 },
      px: { xs: 1, sm: 2 },
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 0, sm: 2 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 6 } }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 'bold', 
            color: 'primary.main', 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            background: 'linear-gradient(45deg, #3b5dac 30%, #1677ff 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            إنشاء حساب جديد
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ 
            opacity: 0.8,
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
            px: { xs: 2, sm: 0 }
          }}>
            أدخل بياناتك لإنشاء حساب في متجر الصادق
          </Typography>
        </Box>

        {/* Signup Form */}
        <Card sx={{ 
          mb: { xs: 2, sm: 3, md: 4 },
          boxShadow: (theme) => theme.shadows[10], 
          borderRadius: { xs: 2, sm: 3, md: 4 },
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
            {/* Progress Stepper */}
            <Stepper activeStep={currentStep} sx={{ mb: 4 }} alternativeLabel>
              <Step>
                <StepLabel>معلومات التسجيل</StepLabel>
              </Step>
              <Step>
                <StepLabel>التحقق من الهاتف</StepLabel>
              </Step>
              <Step>
                <StepLabel>اكتمال التسجيل</StepLabel>
              </Step>
            </Stepper>

            {/* Step 0: Registration Form */}
            {currentStep === 0 && (
              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  {/* Full Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="الاسم الكامل *"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      )
                    }}
                    placeholder="مثال: أحمد محمد علي"
                    variant="outlined"
                    required
                    disabled={loading}
                    autoComplete="name"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#fff',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف *"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      )
                    }}
                    placeholder="مثال: 07701234567"
                    variant="outlined"
                    required
                    disabled={loading}
                    autoComplete="tel"
                    helperText="رقم عراقي يبدأ بـ 07 (سيتم استخدامه كاسم مستخدم)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#fff',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required disabled={loading}>
                    <InputLabel>المدينة *</InputLabel>
                    <Select
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      label="المدينة *"
                      startAdornment={
                        <InputAdornment position="start">
                          <LocationIcon sx={{ color: 'primary.main', ml: 1 }} />
                        </InputAdornment>
                      }
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#fff',
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>اختر المدينة</em>
                      </MenuItem>
                      <MenuItem value="Baghdad">بغداد</MenuItem>
                      <MenuItem value="Basra">البصرة</MenuItem>
                      <MenuItem value="Erbil">أربيل</MenuItem>
                      <MenuItem value="Najaf">النجف</MenuItem>
                      <MenuItem value="Karbala">كربلاء</MenuItem>
                      <MenuItem value="Sulaymaniyah">السليمانية</MenuItem>
                      <MenuItem value="Mosul">الموصل</MenuItem>
                      <MenuItem value="Duhok">دهوك</MenuItem>
                      <MenuItem value="Babylon">بابل</MenuItem>
                      <MenuItem value="Kirkuk">كركوك</MenuItem>
                      <MenuItem value="DhiQar">ذي قار</MenuItem>
                      <MenuItem value="Muthanna">المثنى</MenuItem>
                      <MenuItem value="Qadisiyyah">القادسية</MenuItem>
                      <MenuItem value="Maysan">ميسان</MenuItem>
                      <MenuItem value="Saladin">صلاح الدين</MenuItem>
                      <MenuItem value="Diyala">ديالى</MenuItem>
                      <MenuItem value="Anbar">الأنبار</MenuItem>
                      <MenuItem value="Wasit">واسط</MenuItem>
                      <MenuItem value="Other">مدينة أخرى</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Address */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="العنوان"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeIcon sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      )
                    }}
                    placeholder="مثال: شارع الرشيد، منطقة الكرادة"
                    variant="outlined"
                    disabled={loading}
                    autoComplete="street-address"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#fff',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Password */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="كلمة المرور *"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                            disabled={loading}
                            sx={{ color: 'primary.main' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    placeholder="أدخل كلمة المرور"
                    variant="outlined"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    helperText="6 أحرف على الأقل"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#fff',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Confirm Password */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="تأكيد كلمة المرور *"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleConfirmPassword}
                            edge="end"
                            disabled={loading}
                            sx={{ color: 'primary.main' }}
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    placeholder="أعد إدخال كلمة المرور"
                    variant="outlined"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#fff',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                sx={(theme) => ({
                  background: 'linear-gradient(45deg, #28a745 30%, #20c997 90%)',
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 'bold',
                  mt: { xs: 2, sm: 3 },
                  boxShadow: theme.shadows[6],
                  '&:hover': {
                    background: 'linear-gradient(45deg, #218838 30%, #17a2b8 90%)',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[10],
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  transition: 'all 0.3s ease',
                })}
              >
                {loading ? 'جارٍ التحقق...' : 'متابعة - التحقق من الهاتف'}
              </Button>
            </Box>
            )}

            {/* Step 1: WhatsApp Verification */}
            {currentStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 }, textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                <WhatsAppIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#25D366', alignSelf: 'center' }} />
                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold', 
                  color: '#25D366',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}>
                  التحقق عبر واتساب
                </Typography>
                
                {!codeSent ? (
                  <>
                    <Typography variant="body1" color="text.secondary">
                      سنرسل لك رمز التحقق المكون من 6 أرقام عبر واتساب إلى:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatPhoneDisplay(formData.phone)}
                      </Typography>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => sendWhatsAppVerification(formData.phone)}
                      disabled={isVerifying}
                      startIcon={isVerifying ? <CircularProgress size={20} /> : <WhatsAppIcon />}
                      sx={{ 
                        backgroundColor: '#25D366',
                        py: 2,
                        '&:hover': { backgroundColor: '#1fb855' }
                      }}
                    >
                      {isVerifying ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => setCurrentStep(0)}
                      disabled={isVerifying}
                    >
                      العودة للتعديل على البيانات
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#25D366' }} />
                        تم إرسال رمز التحقق إلى واتساب
                      </Typography>
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="رمز التحقق"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      inputProps={{ 
                        maxLength: 6,
                        style: { 
                          textAlign: 'center', 
                          fontSize: window.innerWidth < 600 ? '1.5rem' : '2rem', 
                          letterSpacing: window.innerWidth < 600 ? '0.5rem' : '1rem' 
                        }
                      }}
                      helperText={`${remainingAttempts} محاولات متبقية`}
                      sx={{ mb: 2 }}
                    />
                    
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={verifyWhatsAppCode}
                      disabled={!verificationCode || verificationCode.length !== 6 || isVerifying}
                      startIcon={isVerifying ? <CircularProgress size={20} /> : <SecurityIcon />}
                      sx={{ 
                        mb: 2, 
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.95rem', sm: '1rem' }
                      }}
                    >
                      {isVerifying ? 'جاري التحقق...' : 'تأكيد الرمز'}
                    </Button>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Button
                        variant="text"
                        onClick={() => sendWhatsAppVerification(formData.phone)}
                        disabled={remainingTime > 0 || isVerifying}
                        size="small"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                      >
                        {remainingTime > 0 
                          ? `إعادة الإرسال خلال ${Math.floor(remainingTime / 60)}:${(remainingTime % 60).toString().padStart(2, '0')}`
                          : 'إعادة إرسال الرمز'
                        }
                      </Button>
                      
                      <Button
                        variant="text"
                        onClick={() => {
                          setCodeSent(false);
                          setVerificationCode("");
                          setCurrentStep(0);
                        }}
                        size="small"
                      >
                        تغيير البيانات
                      </Button>
                    </Box>
                    
                    {remainingAttempts === 0 && (
                      <Chip 
                        label="تم تجاوز عدد المحاولات المسموحة" 
                        color="error" 
                        sx={{ width: '100%' }}
                      />
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Step 2: Success */}
            {currentStep === 2 && (
              <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 1, sm: 2 } }}>
                <CheckCircleIcon sx={{ fontSize: { xs: 80, sm: 100 }, color: 'success.main', mb: { xs: 2, sm: 3 } }} />
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: 'success.main', 
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                }}>
                  تم إنشاء الحساب بنجاح!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }}>
                  تم التحقق من رقم الهاتف وإنشاء حساب جديد
                </Typography>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, sm: 3 } }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4, 
              borderRadius: 3,
              boxShadow: (theme) => theme.shadows[4],
            }}
          >
            {error}
          </Alert>
        )}

        {/* Login Link */}
        <Card sx={{ 
          boxShadow: (theme) => theme.shadows[4], 
          borderRadius: { xs: 2, sm: 3, md: 4 },
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ 
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}>
              لديك حساب بالفعل؟
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={handleLoginClick}
              startIcon={<LoginIcon />}
              sx={(theme) => ({
          borderColor: 'primary.main',
          color: 'white',
          backgroundColor: 'primary.main',
          px: { xs: 3, sm: 4 },
          py: { xs: 1.2, sm: 1.5 },
          fontSize: { xs: '0.9rem', sm: '1rem' },
          fontWeight: 'bold',
          borderWidth: 2,
          '&:hover': {
            backgroundColor: 'primary.dark',
            color: 'white',
            borderWidth: 2,
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[8],
          },
          transition: 'all 0.3s ease',
              })}
            >
              تسجيل الدخول
            </Button>
          </CardContent>
        </Card>
        
        {/* Success Dialog */}
        <Dialog
          open={showSuccessDialog}
          onClose={handleSuccessDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[8],
              backgroundColor: '#fff',
            }
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              backgroundColor: 'primary.main',
              color: 'white',
              py: 4,
              borderRadius: 0,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              تم إنشاء حسابك بنجاح!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95 }}>
              مرحباً بك في متجر الصادق
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ p: 4, backgroundColor: '#fafafa' }}>
            {/* Username Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ 
                color: 'text.primary', 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600
              }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                اسم المستخدم
              </Typography>
              <Box 
                sx={(theme) => ({ 
                  display: 'flex', 
                  alignItems: 'center',
                  backgroundColor: '#ffffff',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 2.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                })}
              >
                <Typography variant="h5" sx={{ 
                  flex: 1, 
                  fontFamily: 'monospace', 
                  color: 'text.primary',
                  fontWeight: 'bold'
                }}>
                  {userCredentials.username}
                </Typography>
                <IconButton 
                  onClick={() => copyToClipboard(userCredentials.username, 'اسم المستخدم')}
                  sx={{ color: 'primary.main' }}
                  title="نسخ اسم المستخدم"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Password Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ 
                color: 'text.primary', 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600
              }}>
                <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                كلمة المرور
              </Typography>
              <Box 
                sx={(theme) => ({ 
                  display: 'flex', 
                  alignItems: 'center',
                  backgroundColor: '#ffffff',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 2.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                })}
              >
                <Typography variant="h5" sx={{ 
                  flex: 1, 
                  fontFamily: 'monospace', 
                  color: 'text.primary',
                  fontWeight: 'bold'
                }}>
                  {userCredentials.password}
                </Typography>
                <IconButton 
                  onClick={() => copyToClipboard(userCredentials.password, 'كلمة المرور')}
                  sx={{ color: 'primary.main' }}
                  title="نسخ كلمة المرور"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Important Instructions */}
            <Box 
              sx={(theme) => ({ 
                backgroundColor: '#fff3cd',
                border: '1px solid',
                borderColor: 'warning.main',
                borderRadius: 1,
                p: 3,
                mb: 2,
              })}
            >
              <Typography variant="h6" sx={{ 
                color: 'warning.dark', 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600
              }}>
                <SecurityIcon sx={{ mr: 1 }} />
                مهم جداً!
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <PhotoCameraIcon sx={{ color: 'warning.dark', mr: 1.5, mt: 0.5 }} />
                <Typography variant="body1" sx={{ 
                  color: 'warning.dark', 
                  lineHeight: 1.8,
                  fontWeight: 400
                }}>
                  قم بأخذ لقطة شاشة لهذه الصفحة أو احفظ هذه البيانات في مكان آمن
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ 
                color: 'warning.dark', 
                fontWeight: 600,
                textAlign: 'center',
                fontSize: '1rem'
              }}>
                ستحتاج إلى هذه البيانات لتسجيل الدخول مرة أخرى
              </Typography>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 4, justifyContent: 'center', backgroundColor: '#fafafa' }}>
            <Button
              onClick={handleSuccessDialogClose}
              variant="contained"
              size="large"
              sx={(theme) => ({
                backgroundColor: 'primary.main',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: theme.shadows[2],
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: theme.shadows[4],
                },
                transition: 'all 0.2s ease',
              })}
            >
              فهمت، أريد المتابعة للمتجر
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Signup;
